import { action, computed, makeObservable, observable } from 'mobx';
import {
  ERROR_CODES,
  extractServerErrorCode,
  extractErrorKeyByErrorCode,
} from '../utils/errorHandler';
import { RootStore } from './root';
import { AuthenticateByCodeStore } from './authenticateByCode';
import {
  loginTwoFactorAuthenticationVerifyClick,
  loginTwoFactorAuthenticationVerifySuccess,
  loginTwoFactorAuthenticationVerifyFail,
  loginTwoFactorAuthenticationResendCodeClick,
  loginTwoFactorAuthenticationLostPhoneAccessClick,
} from '@wix/bi-logger-hls2/v2';
import {
  NEW_ACCOUNT_RECOVERY_URL,
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethods,
  twoFAProcessMethod,
  TWOFA_PROCESS,
  TwoFactorAuthMethod,
  EXPERIMENTS,
  twoFactorDescriptionKeysMap,
} from '../utils/constants';
import { useContext } from 'react';
import { AppContextProvider } from '../components/AppLoader';
import {
  StateMachineResponse,
  FactorType,
  MfaReason,
  FactorStatus,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { ApprovalChallengeStatus } from './premiumUsers2FaEnforcement/ownerApp/types';

const POLLING_INTERVAL = 2000;

type FactorMap = {
  [key in FactorType]: TwoFactorAuthMethod;
};
type MfaFactorMap = Omit<
  FactorMap,
  FactorType.PASSWORD | FactorType.UNKNOWN_FACTOR_TYPE | FactorType.CALL
>;
const factorMap: MfaFactorMap = {
  [FactorType.TOTP]: TwoFactorAuthMethods.TOTP,
  [FactorType.SMS]: TwoFactorAuthMethods.Phone,
  [FactorType.EMAIL]: TwoFactorAuthMethods.Email,
  [FactorType.PUSH]: TwoFactorAuthMethods.Push,
  [FactorType.WEBAUTHN]: TwoFactorAuthMethods.WebAuthn,
  [FactorType.RECOVERY_CODE]: TwoFactorAuthMethods.RecoveryCode,
};

export interface WebAuthn2FAChallengeData {
  challengeId?: string;
  publicKeyCredentialRequestOptionsJson?: string;
}

export class TwoFactorAuthStore extends AuthenticateByCodeStore {
  twoFAToken: string;
  stateToken?: string;
  verificationId: string | undefined | null;
  uaToken: string | undefined;
  deviceName: string;
  userId: string;
  usingTwoFAFallback: boolean;
  allowRememberMe: boolean = true;
  lfndForce2FA?: boolean;
  dialogTitleKey: string = 'enter_code.title';
  twoFactorAuthStatus: ApprovalChallengeStatus =
    ApprovalChallengeStatus.PENDING_APPROVAL;
  pollingTimer: NodeJS.Timeout | null = null;
  isFetchingStateToken: boolean = false;
  isCheckingStatusInProgress: boolean = false;
  phoneDeliveryMethod?: string;
  webauthnChallengeData?: WebAuthn2FAChallengeData;
  isLoading: boolean = false;

  private urlParams: URLSearchParams = new URLSearchParams(
    window.location.search,
  );
  private isDebug: boolean = this.urlParams.get('debugCase') === 'mfa';
  private testPayload: Partial<TwoFactorAuthPayload> = {
    currentTwoFAMethod: this.urlParams.get('method'),
    enabledTwoFAMethods: this.urlParams.get('enabled')?.split(','),
    twoFAHint: this.urlParams.get('hint') ?? '',
  };

  constructor(rootStore: RootStore) {
    super(rootStore);
    this.SUBMIT_INTERACTION_NAME = 'verify-code-2fa';
    this.isDebug && this.setAuthParams(this.testPayload);
    makeObservable(this, {
      currentTwoFAMethod: observable,
      enabledTwoFAMethods: observable,
      twoFAHint: observable,
      twoFAToken: observable,
      usingTwoFAFallback: observable,
      twoFactorAuthStatus: observable,
      showMethodsSwitch: computed,
      setAuthParams: action,
      verify: action,
      resendCode: action,
      onLostAccess: action.bound,
      stateToken: observable,
      phoneDeliveryMethod: observable,
      webauthnChallengeData: observable,
      isLoading: observable,
      verifyWithWebAuthn: action,
    });
  }

  getResendTimerInitialValue() {
    return this.rootStore.postLoginStore.shouldShowWixAppEnforcement ? 10 : 30;
  }

  setAuthParamsMfaIAM(payload: StateMachineResponse) {
    this.stateToken = payload.stateToken as string;
    const currentFactor = payload.mfaChallengeData?.factorType;
    const availableFactors = payload.mfaChallengeData?.availableFactors;
    if (
      this.rootStore.experiments.enabled(
        EXPERIMENTS.SHOULD_SUPPORT_FALLBACK_FACTOR,
      )
    ) {
      this.usingTwoFAFallback = this.calculateUsingTwoFAFallback(
        currentFactor,
        availableFactors,
      );
      this.enabledTwoFAMethods = this.calculateEnabledTwoFAMethods(
        availableFactors,
        this.currentTwoFAMethod,
      );
    } else {
      this.enabledTwoFAMethods =
        Array.isArray(availableFactors) && availableFactors.length > 0
          ? availableFactors.map((factor) =>
              factor.factorType
                ? factorMap[factor.factorType]
                : TwoFactorAuthMethods.Unused,
            )
          : [this.currentTwoFAMethod];
    }
    this.currentTwoFAMethod = currentFactor
      ? factorMap[currentFactor]
      : TwoFactorAuthMethods.Unused;
    this.twoFAToken = payload?.additionalData?.legacy2FAToken
      ?.strValue as string;
    this.twoFAHint = payload.mfaChallengeData?.verificationChallengeData?.hint;
    this.verificationId =
      payload.mfaChallengeData?.verificationChallengeData?.pushData?.transactionId;
    this.deviceName = this.getDeviceName(this.twoFAHint!);
    this.lfndForce2FA =
      payload.mfaChallengeData?.reason === MfaReason.HIGH_RISK_LOGIN;
  }

  private calculateUsingTwoFAFallback(currentFactor, availableFactors) {
    return (
      currentFactor === FactorType.EMAIL &&
      !!availableFactors?.some(
        (factor) =>
          factor.factorType === FactorType.SMS &&
          factor.factorStatus === FactorStatus.DISABLED_BY_RULE,
      )
    );
  }

  private calculateEnabledTwoFAMethods(availableFactors, currentMethod) {
    return Array.isArray(availableFactors) && availableFactors.length > 0
      ? availableFactors
          .filter(
            (factor) => factor.factorStatus !== FactorStatus.DISABLED_BY_RULE,
          )
          .map((factor) =>
            factor.factorType
              ? factorMap[factor.factorType]
              : TwoFactorAuthMethods.Unused,
          )
      : [currentMethod];
  }

  setAuthParams(payload: Partial<TwoFactorAuthPayload>) {
    if (payload.stateToken) {
      this.stateToken = payload.stateToken;
    }
    this.twoFAHint = payload.twoFAHint!;
    this.twoFAToken = payload.twoFAToken!;
    this.usingTwoFAFallback = this.isDebug
      ? false
      : !!payload.usingTwoFAFallback;
    if (this.isDebug) {
      this.currentTwoFAMethod = this.urlParams.get('method') as any;
    } else {
      this.currentTwoFAMethod =
        this.usingTwoFAFallback || !payload.currentTwoFAMethod
          ? TwoFactorAuthMethods.Email
          : payload.currentTwoFAMethod;
    }
    this.rootStore.fedopsLogger.interactionStarted(
      twoFAProcessMethod(this.currentTwoFAMethod),
    );
    this.enabledTwoFAMethods = payload.enabledTwoFAMethods ?? [
      this.currentTwoFAMethod,
    ];
    this.verificationId = payload.verificationId;
    this.uaToken = payload.uaToken;
    this.deviceName = this.getDeviceName(payload.twoFAHint!);
    this.lfndForce2FA = payload.lfndForce2FA;
  }

  getDeviceName(twoFAHint: string) {
    try {
      const { deviceBrandName, deviceModelName } = JSON.parse(twoFAHint);
      return `${deviceBrandName} ${deviceModelName}`;
    } catch (error) {
      return '';
    }
  }

  onResendCodeClicked(
    options: { phoneDeliveryMethod?: string } = {
      phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
    },
  ) {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationResendCodeClick({
        flow_type: this.currentTwoFAMethod,
        tfa_type: 'login',
        reason: this.lfndForce2FA ? 'LFND' : 'login',
      }),
    );
    return this.resendCode(options);
  }

  getResendButtonKey() {
    if (this.twoFactorAuthStatus === ApprovalChallengeStatus.PENDING_APPROVAL) {
      return 'twoFactorAuth.resendNotification';
    }

    return 'twoFactorAuth.resendLoginRequest';
  }

  waitForStateToken = async () => {
    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (!this.isFetchingStateToken) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  };

  async resendCode(
    options: { phoneDeliveryMethod?: string } = {
      phoneDeliveryMethod: TwoFactorAuthPhoneDeliveryMethods.SMS,
    },
  ) {
    const { phoneDeliveryMethod } = options;
    this.phoneDeliveryMethod = phoneDeliveryMethod;

    try {
      if (
        this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
      ) {
        await this.waitForStateToken();
      }
      const data = await this.rootStore.apiStore.resendSecondFactorCode({
        twoFAToken: this.twoFAToken,
        twoFAMethod: this.currentTwoFAMethod,
        phoneDeliveryMethod,
        stateToken: this.stateToken,
      });

      if (data?.payload?.stateToken) {
        this.stateToken = data.payload?.stateToken;
      }

      if (data?.payload?.currentTwoFAMethod === TwoFactorAuthMethods.Push) {
        this.setAuthParams(data.payload);
        this.setTwoFactorAuthStatus(ApprovalChallengeStatus.PENDING_APPROVAL);
        this.startPolling();
      }

      if (data?.payload?.twoFAHint) {
        this.twoFAHint = data.payload?.twoFAHint;
      }

      if (data?.payload?.twoFAToken) {
        this.twoFAToken = data?.payload?.twoFAToken ?? '';
      } else {
        return extractServerErrorCode(data);
      }
    } catch (error: any) {
      return extractServerErrorCode(error);
    }
  }

  /**
   * Fetch challenge data for a 2FA method.
   * Updates stateToken and twoFAToken in the store (server may invalidate old tokens).
   * Returns the full payload for the caller to use as needed.
   * @throws {Error} with error code as message on failure
   */
  private async fetchMethodChallenge(
    method: TwoFactorAuthMethod,
    phoneDeliveryMethod?: string,
  ): Promise<{
    stateToken?: string;
    twoFAToken: string;
    twoFAHint?: string;
    webauthnChallengeData?: WebAuthn2FAChallengeData;
  }> {
    if (
      this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
    ) {
      await this.waitForStateToken();
    }

    let data;
    try {
      data = await this.rootStore.apiStore.resendSecondFactorCode({
        twoFAToken: this.twoFAToken,
        twoFAMethod: method,
        phoneDeliveryMethod,
        stateToken: this.stateToken,
      });
    } catch (error: any) {
      throw new Error(extractServerErrorCode(error));
    }

    if (!data?.payload?.twoFAToken) {
      throw new Error(extractServerErrorCode(data));
    }

    // Always update tokens - server may invalidate old ones
    if (data.payload.stateToken) {
      this.stateToken = data.payload.stateToken;
    }
    if (data.payload.verificationId) {
      this.verificationId = data.payload.verificationId;
    }
    this.twoFAToken = data.payload.twoFAToken;

    return {
      stateToken: data.payload.stateToken,
      twoFAToken: data.payload.twoFAToken,
      twoFAHint: data.payload.twoFAHint,
      webauthnChallengeData: data.payload.webauthnChallengeData,
    };
  }

  /**
   * Atomically switch to a new 2FA method.
   * Makes the API call FIRST, then updates state only on success.
   * This prevents the UI from showing stale/empty hints during the transition.
   * @throws {Error} with error code as message on failure
   */
  async switchToMethod(
    newMethod: TwoFactorAuthMethod,
    options: { phoneDeliveryMethod?: string } = {},
  ): Promise<void> {
    const { phoneDeliveryMethod } = options;

    // fetchMethodChallenge updates tokens, we update the rest
    const challengeData = await this.fetchMethodChallenge(
      newMethod,
      phoneDeliveryMethod,
    );

    this.currentTwoFAMethod = newMethod;
    this.phoneDeliveryMethod = phoneDeliveryMethod;

    if (challengeData.twoFAHint) {
      this.twoFAHint = challengeData.twoFAHint;
    }
    if (challengeData.webauthnChallengeData) {
      this.webauthnChallengeData = challengeData.webauthnChallengeData;
    }
  }

  backToLogin() {
    this.rootStore.navigationStore.goToMainLogin({
      email: this.rootStore.userDataStore.email,
    });
  }

  resendOwnerAppNotification = async () => {
    if (this.twoFactorAuthStatus === ApprovalChallengeStatus.EXPIRED) {
      this.twoFactorAuthStatus = ApprovalChallengeStatus.PENDING_APPROVAL;
    }
    if (
      this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
    ) {
      this.stopPolling();
      await this.waitForStateToken();
    }
    const { payload } = await this.rootStore.apiStore.resendSecondFactorCode({
      twoFAToken: this.twoFAToken,
      twoFAMethod: this.currentTwoFAMethod,
      stateToken: this.stateToken,
    });
    if (payload?.stateToken) {
      this.stateToken = payload?.stateToken;
    }
    this.setAuthParams(payload);
    this.startPolling();
  };

  verify({ code, rememberMe }: { code: string; rememberMe?: boolean }) {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationVerifyClick({
        remember_this_device: rememberMe,
        flow_type: this.currentTwoFAMethod,
        tfa_type: 'login',
        view: 'login',
      }),
    );
    this.rootStore.fedopsLogger.interactionStarted(
      this.SUBMIT_INTERACTION_NAME,
    );
    return this.rootStore.apiStore
      .verifyCodeAndLogin({
        stateToken: this.stateToken,
        twoFAMethod: this.currentTwoFAMethod,
        twoFAToken: this.twoFAToken,
        code,
        rememberMe,
        phoneDeliveryMethod: this.phoneDeliveryMethod,
      })
      .then((data) => {
        if (data.success) {
          this.rootStore.fedopsLogger.interactionEnded(
            this.SUBMIT_INTERACTION_NAME,
          );
          this.rootStore.fedopsLogger.interactionEnded(TWOFA_PROCESS);
          this.rootStore.fedopsLogger.interactionEnded(
            twoFAProcessMethod(this.currentTwoFAMethod),
          );
          this.rootStore.biLogger.report(
            loginTwoFactorAuthenticationVerifySuccess({
              flow_type: this.currentTwoFAMethod,
              reason: this.lfndForce2FA ? 'LFND' : 'login',
              view: 'login',
            }),
          );
          const finalData = data?.payload ?? data?.data;
          this.rootStore.navigationStore.postLogin(finalData);
          return;
        }
        this.rootStore.biLogger.report(
          loginTwoFactorAuthenticationVerifyFail({
            flow_type: this.currentTwoFAMethod,
            reason: this.lfndForce2FA ? 'LFND' : 'login',
            view: 'login',
          }),
        );
        const errorCode = extractServerErrorCode(data);
        this.handleError(errorCode);
        throw { errorCode };
      })
      .catch((error) => {
        if (error?.errorCode === ERROR_CODES.VALIDATION_ERROR_TOTP_IAM) {
          this.handleError(error.errorData?.fieldViolations[0]?.ruleName);
        }
        if (error?.errorData === ERROR_CODES.TOKEN_EXPIRED_IAM) {
          this.handleError(error.errorData);
        }
        if (!error?.errorCode) {
          this.handleError(ERROR_CODES.GENERAL_ERROR_CODE);
        }
        this.handleError(error.errorCode);
        throw new Error(error);
      });
  }

  private showToastError(errorCode: string): void {
    const errorKey = extractErrorKeyByErrorCode(errorCode);
    if (errorKey) {
      this.rootStore.notificationStore.openErrorToast(
        this.rootStore.i18n.t(errorKey),
      );
    }
  }

  private extractPasskeyErrorCode(error: unknown): string {
    const errorCode =
      error instanceof Error ? error.message : extractServerErrorCode(error);
    const hasKnownErrorKey = !!extractErrorKeyByErrorCode(errorCode, {
      defaultError: '',
    });

    if (errorCode !== ERROR_CODES.GENERAL_ERROR_CODE && hasKnownErrorKey) {
      return errorCode;
    }

    // Keep parity with the main login passkey flow for generic/unknown errors.
    return ERROR_CODES.TECHNICAL_ERROR;
  }

  /**
   * Submit WebAuthn credential to server.
   * Manages its own fedops interaction lifecycle.
   * @returns login data on success, null on failure (error already handled)
   */
  private async submitWebAuthnCredential(
    webauthnData: { challengeId: string; credential: string },
    twoFAMethod: TwoFactorAuthMethod,
    rememberMe?: boolean,
  ): Promise<any | null> {
    this.rootStore.fedopsLogger.interactionStarted(
      this.SUBMIT_INTERACTION_NAME,
    );

    try {
      const data = await this.rootStore.apiStore.verifyCodeAndLogin({
        stateToken: this.stateToken,
        twoFAMethod,
        twoFAToken: this.twoFAToken,
        rememberMe,
        webauthnData,
      });

      if (data.success) {
        this.rootStore.fedopsLogger.interactionEnded(
          this.SUBMIT_INTERACTION_NAME,
        );
        return data?.payload ?? data?.data;
      }

      throw new Error(extractServerErrorCode(data));
    } catch (error) {
      this.rootStore.fedopsLogger.interactionEnded(
        this.SUBMIT_INTERACTION_NAME,
      );
      throw error;
    }
  }

  /**
   * Complete 2FA WebAuthn/passkey verification flow.
   * Gets challenge, authenticates with browser, and completes verification.
   * Does NOT change the current 2FA method - passkey runs independently.
   * Shows error notification on failure.
   */
  async verifyWithWebAuthn(rememberMe?: boolean): Promise<void> {
    // Prevent multiple simultaneous WebAuthn verification attempts
    if (this.isLoading) {
      return;
    }

    const apiTwoFAMethod = TwoFactorAuthMethods.WebAuthn;
    const biFlowType = 'Passkey';

    const reportStart = () => {
      this.rootStore.biLogger.report(
        loginTwoFactorAuthenticationVerifyClick({
          remember_this_device: rememberMe,
          flow_type: biFlowType,
          tfa_type: 'login',
          view: 'login',
        }),
      );
    };

    const reportSuccess = () => {
      this.rootStore.fedopsLogger.interactionEnded(TWOFA_PROCESS);
      this.rootStore.fedopsLogger.interactionEnded(
        twoFAProcessMethod(apiTwoFAMethod),
      );
      this.rootStore.biLogger.report(
        loginTwoFactorAuthenticationVerifySuccess({
          flow_type: biFlowType,
          reason: this.lfndForce2FA ? 'LFND' : 'login',
          view: 'login',
        }),
      );
    };

    const reportError = (errorCode: string) => {
      this.rootStore.biLogger.report(
        loginTwoFactorAuthenticationVerifyFail({
          flow_type: biFlowType,
          reason: this.lfndForce2FA ? 'LFND' : 'login',
          view: 'login',
        }),
      );
      this.showToastError(errorCode);
    };

    // Main flow
    reportStart();
    this.rootStore.notificationStore.close();
    this.isLoading = true;

    try {
      // 1. Fetch WebAuthn challenge (also updates tokens in store)
      const challengeData = await this.fetchMethodChallenge(apiTwoFAMethod);
      if (!challengeData.webauthnChallengeData) {
        reportError(ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED);
        return;
      }

      // 2. Authenticate with browser
      const result =
        await this.rootStore.webauthnStore.authenticateWith2FAChallenge(
          challengeData.webauthnChallengeData,
        );
      if (result.status === 'cancelled') {
        return;
      }
      if (result.status === 'error') {
        reportError(result.errorCode);
        return;
      }

      // 3. Submit credential to server
      const loginData = await this.submitWebAuthnCredential(
        result.data,
        apiTwoFAMethod,
        rememberMe,
      );

      reportSuccess();
      this.rootStore.navigationStore.postLogin(loginData);
    } catch (error) {
      reportError(this.extractPasskeyErrorCode(error));
    } finally {
      this.isLoading = false;
    }
  }

  showResendButton() {
    return this.currentTwoFAMethod !== TwoFactorAuthMethods.TOTP;
  }

  getTitleKey() {
    if (this.lfndForce2FA) {
      return 'enter_code.lfnd.title';
    }
    return this.usingTwoFAFallback
      ? 'enter_code.fallback.title'
      : 'enter_code.title';
  }

  getDescriptionKey() {
    if (this.lfndForce2FA) {
      return 'enter_code.lfnd.description';
    }
    return this.usingTwoFAFallback
      ? 'enter_code.fallback.description'
      : twoFactorDescriptionKeysMap[this.currentTwoFAMethod];
  }

  enableAccess() {
    return !!(this.twoFAToken && this.currentTwoFAMethod) || this.isDebug;
  }

  onLostAccess() {
    this.rootStore.biLogger.report(
      loginTwoFactorAuthenticationLostPhoneAccessClick({
        flow_type: this.currentTwoFAMethod,
      }),
    );
    const redirectUrl = `${NEW_ACCOUNT_RECOVERY_URL}/two-fa-pre-screen?${new URLSearchParams(
      {
        from: window.location.href,
        sessionId: this.rootStore.sessionId.get(),
        email: this.rootStore.userDataStore.email,
      },
    ).toString()}`;
    this.rootStore.navigationStore.redirect(redirectUrl);
  }

  public async getApprovalChallengeStatus(): Promise<
    ApprovalChallengeStatus | undefined
  > {
    try {
      if (!this.verificationId) {
        return undefined;
      }
      this.isFetchingStateToken = true;
      const { response, stateToken } =
        await this.rootStore.apiStore.getChallenge(
          this.verificationId,
          this.stateToken,
        );
      if (stateToken) {
        this.stateToken = stateToken;
      }
      return response?.challenge?.approvalChallenge?.status;
    } catch (error) {
      return undefined;
    } finally {
      this.isFetchingStateToken = false;
    }
  }

  get showMethodsSwitch() {
    return !this.usingTwoFAFallback && this.enabledTwoFAMethods?.length! > 1;
  }

  setTwoFactorAuthStatus(status: ApprovalChallengeStatus) {
    this.twoFactorAuthStatus = status;
  }

  async checkStatus() {
    const verificationStatus = await this.getApprovalChallengeStatus();
    if (verificationStatus === ApprovalChallengeStatus.APPROVED) {
      this.stopPolling();
      let shouldCallVerify = true;
      let uaToken;
      if (
        this.currentTwoFAMethod === TwoFactorAuthMethods.Push &&
        this.stateToken !== undefined &&
        this.rootStore.experiments.enabled(EXPERIMENTS.USE_IAM_FOR_PUSH_FACTOR)
      ) {
        shouldCallVerify = false;
      }
      if (shouldCallVerify) {
        const { payload } = await this.rootStore.apiStore.verifyCodeAndLogin({
          stateToken: this.stateToken,
          twoFAMethod: this.currentTwoFAMethod,
          twoFAToken: this.twoFAToken,
          rememberMe: false,
        });
        uaToken = payload.uaToken;
      }
      await this.rootStore.navigationStore.postLogin({
        uaToken,
      });
    }
    if (verificationStatus === ApprovalChallengeStatus.EXPIRED) {
      this.setTwoFactorAuthStatus(ApprovalChallengeStatus.EXPIRED);
      this.stopPolling();
    }
    if (verificationStatus === ApprovalChallengeStatus.DECLINED) {
      this.setTwoFactorAuthStatus(ApprovalChallengeStatus.DECLINED);
      this.stopPolling();
    }
  }

  private scheduleNextPoll() {
    this.pollingTimer = setTimeout(async () => {
      if (!this.isCheckingStatusInProgress) {
        this.isCheckingStatusInProgress = true;
        await this.checkStatus();
        this.isCheckingStatusInProgress = false;
      }
      // Only schedule next poll if we haven't been stopped
      if (this.pollingTimer !== null) {
        this.scheduleNextPoll();
      }
    }, POLLING_INTERVAL);
  }

  public startPolling() {
    this.stopPolling();
    this.scheduleNextPoll();
  }

  public stopPolling() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
}

export const useTwoFactorAuth = () => {
  const {
    rootStore: { twoFactorAuthStore, biLogger },
  } = useContext(AppContextProvider);

  return { store: twoFactorAuthStore, biLogger };
};
